import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CreateOrEditRoutes from "./CreateOrEditRoutes";
import { AdminResourcePath } from "@/declarations/AdminResourcePath";
import type { DataListProps } from "@/components/DataList/DataList";
import Api from "@/services/Api";
import type { DataListRow } from "@/components/DataList/DataListRow";
import type { CreateOrEditProps } from "@/components/CreateOrEdit";
import { schema } from "@/declarations/schemas/identifier_type/schema";
import { uiSchema } from "@/declarations/schemas/identifier_type/uiSchema";
import type { RequestContext } from "@/declarations/RequestContext";
import BreadcrumbNode from "@/components/BreadcrumbNode";
import type { SortOptions } from "@/components/DataList/ListHeader/SortSelector";
import { useDebounce } from "@/hooks/useDebounce";
import type { SearchProp } from "@/components/DataList/ListHeader/DataListHeader";
import { useSortAndFilterState } from "@/hooks/useSortAndFilterState";
import type { IdentifierType } from "@/declarations/models/IdentifierType";

const mapperFn = (identifierTypeItem: IdentifierType): DataListRow => ({
  key: String(identifierTypeItem.id),
  title: identifierTypeItem.identifier_type,
  subTitle: identifierTypeItem.description,
  infoText: `ID: ${identifierTypeItem.id}`,
  updatedAt: identifierTypeItem.updated_at,
  updatedBy: identifierTypeItem.updated_by,
});

const customSortOptions: Array<SortOptions> = [
  { prop: "identifier_type", direction: "asc", label: "components.list.sort.byTitleAsc" },
  { prop: "identifier_type", direction: "desc", label: "components.list.sort.byTitleDesc" },
  { prop: "updated_at", direction: "asc", label: "components.list.sort.byUpdatedDateAsc" },
  { prop: "updated_at", direction: "desc", label: "components.list.sort.byUpdatedDateDesc" },
  { prop: "description", direction: "asc", label: "components.list.sort.byDescriptionAsc" },
  { prop: "description", direction: "desc", label: "components.list.sort.byDescriptionDesc" },
];

const getIdentifierType: CreateOrEditProps<IdentifierType>["getFormData"] = (
  id: number
): RequestContext<IdentifierType> => Api.getOneIdentifierType(id);
const updateIdentifierType: CreateOrEditProps<IdentifierType>["onSubmit"] = async (
  identifierType: IdentifierType
): Promise<RequestContext<IdentifierType>> => {
  return Api.updateIdentifierType(identifierType);
};
const createIdentifierType: CreateOrEditProps<IdentifierType>["onSubmit"] = (
  identifierType: IdentifierType
): RequestContext<IdentifierType> => Api.createIdentifierType(identifierType);
const deleteIdentifierType = async (identifierType: IdentifierType) => {
  const error = (await Api.deleteIdentifierType(identifierType?.id || 0).fetch())[1];
  if (error) {
    return Promise.reject("Unable to delete identifierType");
  }
};
const undeleteIdentifierType = async (item: IdentifierType) => {
  await Api.undeleteIdentifierType(item.id!).fetch();
};

export const IdentifierTypeView: FC = () => {
  const { t } = useTranslation("common");

  const { setSearchParameters, sortBy, sortAscending, initialSortOption } = useSortAndFilterState({
    sortOptions: customSortOptions,
    defaultSortBy: "identifier_type",
  });
  const [lastFetchedTimestamp, setLastFetchedTimestamp] = useState(Date.now());
  const handleOnItemsChanged = async (sortProp?: string, sortDirection?: string) => {
    setSearchParameters(sortProp, sortDirection);
    setLastFetchedTimestamp(Date.now());
  };

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchTerms, setSearchTerms] = useState<string>("");

  const debouncedSetSearchTerms = useDebounce<string>(500, (t) => {
    setSearchTerms(t || "");
    setLastFetchedTimestamp(Date.now());
  });

  useEffect(() => {
    if (searchInput !== searchTerms) debouncedSetSearchTerms(searchInput);
  }, [debouncedSetSearchTerms, searchInput, searchTerms]);

  const getIdentifierTypes: DataListProps<IdentifierType>["getItems"] = (page, page_size) =>
    Api.getAllIdentifierTypes({
      page,
      page_size,
      sort_by: sortBy,
      order_asc: sortAscending,
    }).fetchDirect({
      page,
      page_size,
      count: 0,
      items: [],
      total_count: 0,
    });

  return (
    <>
      <BreadcrumbNode label={`adminResourcePath.${AdminResourcePath.IDENTIFIER_TYPE}`} />
      <CreateOrEditRoutes
        schema={schema}
        uiSchema={uiSchema}
        goBackOnSubmit
        createFormData={createIdentifierType}
        updateFormData={updateIdentifierType}
        getFormData={getIdentifierType}
        listProps={{
          hideImage: true,
          listTitle: t("schemaTypes.identifier_type_plural"),
          mapperFn,
          getItems: getIdentifierTypes,
          onDeleteItem: deleteIdentifierType,
          onDeleteItemForever: deleteIdentifierType,
          onUndeleteItem: undeleteIdentifierType,
          handleOnItemsChanged,
          externalDataChanged: lastFetchedTimestamp,
          initialSortOption,
          sortOptions: customSortOptions,
          resetPageDeps: [searchTerms],
          searchProp: {
            query: searchInput,
            updateQuery: setSearchInput,
            placeholder: t("search.inputLabel"),
          } as SearchProp,
        }}
      />
    </>
  );
};

export default IdentifierTypeView;
